"use client";
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { LuCalendarDays } from "react-icons/lu";
import { MdOutlineTimer } from "react-icons/md";
import { IoMdTime } from "react-icons/io";
import { GoPencil, GoPlus, GoTrash } from "react-icons/go";
import { FaAngleUp, FaAngleDown } from "react-icons/fa6";
import toast from "react-hot-toast";
import axios from "axios";
import "./EditClientModalCalendar.css";
import { REST_API_URL } from "../api/config";

const API_BASE = REST_API_URL;

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getCurrencySymbol = (code) =>
  CURRENCY_SYMBOL_MAP[code] || code || "";

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

const parseTimeString = (timeString) => {
  if (!timeString) return { hour: 8, minute: 0, ampm: "AM" };
  
  try {
    if (typeof timeString === "string" && timeString.includes(" ")) {
      const [timePart, ampm] = timeString.split(" ");
      const [hour, minute] = timePart.split(":").map(Number);
      
      let hour24 = hour;
      if (ampm === "PM" && hour !== 12) hour24 = hour + 12;
      if (ampm === "AM" && hour === 12) hour24 = 0;
      
      return {
        hour: hour24,
        minute: minute || 0,
        ampm: ampm
      };
    }
    
    if (typeof timeString === "string" && timeString.includes(":")) {
      const [hour, minute] = timeString.split(":").map(Number);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      
      return {
        hour: hour,
        minute: minute || 0,
        ampm: ampm,
        hour12: hour12
      };
    }
    
    if (typeof timeString === "string" && (timeString.includes("T") || timeString.includes(" "))) {
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        const hour = date.getHours();
        const minute = date.getMinutes();
        const ampm = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
        
        return {
          hour: hour,
          minute: minute,
          ampm: ampm,
          hour12: hour12
        };
      }
    }
    
    console.warn("Не удалось распознать формат времени:", timeString);
    return { hour: 8, minute: 0, ampm: "AM" };
  } catch (error) {
    console.error("Ошибка при парсинге времени:", error, timeString);
    return { hour: 8, minute: 0, ampm: "AM" };
  }
};

const formatTimeForDisplay = (hour, minute, ampm) => {
  const hour12 = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${ampm}`;
};

const calculateDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return { hours: 1, minutes: 0 };
  
  try {
    const start = parseTimeString(startTime);
    const end = parseTimeString(endTime);
    
    const startMinutes = start.hour * 60 + start.minute;
    const endMinutes = end.hour * 60 + end.minute;
    
    let diffMinutes = endMinutes - startMinutes;
    if (diffMinutes < 0) diffMinutes += 24 * 60;
    
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    
    return { hours, minutes };
  } catch (error) {
    console.error("Ошибка при вычислении длительности:", error);
    return { hours: 1, minutes: 0 };
  }
};

const formatPriceWithCurrency = (price, currency) => {
  const priceNum = parseFloat(price || 0);
  const symbol = getCurrencySymbol(currency || "USD");
  
  if (priceNum === 0) return "Free";
  
  return `${symbol}${priceNum.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

function CalendarDropdown({ value, onChange, disabled }) {
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(
    value && value.includes(" ") ? value.split(" ")[0] : today.toLocaleString("default", { month: "long" })
  );
  const [selectedDay, setSelectedDay] = useState(
    value && value.includes(" ") ? parseInt(value.split(" ")[1]) : today.getDate()
  );

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  const handleSave = () => {
    onChange(`${selectedMonth} ${selectedDay}`);
    setOpen(false);
  };

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="appointments-client-info-modal__calendar-dropdown" ref={dropdownRef}>
      <div
        className={`appointments-client-info-modal__calendar-input ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setOpen(!open)}
      >
        <LuCalendarDays className="appointments-client-info-modal__calendar-icon" />
        <input type="text" value={`${selectedMonth} ${selectedDay}`} readOnly disabled={disabled} />
        <GoPencil className="appointments-client-info-modal__calendar-edit" />
      </div>

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            className="appointments-client-info-modal__calendar-popup appointments-client-info-modal__calendar-popup_day"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <div className="appointments-client-info-modal__month-selector">
              <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {months.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="appointments-client-info-modal__calendar-days">
              {days.map((d) => (
                <div
                  key={d}
                  className={`appointments-client-info-modal__day ${d === selectedDay ? "active" : ""}`}
                  onClick={() => setSelectedDay(d)}
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="appointments-client-info-modal__calendar-buttons">
              <button onClick={() => setOpen(false)}>Cancel</button>
              <button onClick={handleSave}>Save</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CustomTimePicker({ value = "08:00 AM", onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(8);
  const [minutes, setMinutes] = useState(0);
  const [ampm, setAmpm] = useState("AM");

  const formatTime = (h, m, a) => `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} ${a}`;

  const increase = (type) => {
    if (type === "hour") setHours((h) => h === 12 ? 1 : h + 1);
    else if (type === "minute") setMinutes((m) => (m + 5 >= 60 ? 0 : m + 5));
    else setAmpm((a) => a === "AM" ? "PM" : "AM");
  };
  const decrease = (type) => {
    if (type === "hour") setHours((h) => h === 1 ? 12 : h - 1);
    else if (type === "minute") setMinutes((m) => (m - 5 < 0 ? 55 : m - 5));
    else setAmpm((a) => a === "AM" ? "PM" : "AM");
  };

  const handleSave = () => {
    onChange(formatTime(hours, minutes, ampm));
    setOpen(false);
  };

  useEffect(() => {
    if (value) {
      try {
        const parsed = parseTimeString(value);
        const hour12 = parsed.hour > 12 ? parsed.hour - 12 : (parsed.hour === 0 ? 12 : parsed.hour);
        setHours(hour12);
        setMinutes(parsed.minute);
        setAmpm(parsed.ampm);
      } catch (error) {
        console.error("Ошибка инициализации времени:", error, value);
      }
    }
  }, [value]);

  const timePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="appointments-client-info-modal__time-dropdown" ref={timePickerRef}>
      <div
        className={`appointments-client-info-modal__time-input ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setOpen(!open)}
      >
        <span className="appointments-client-info-modal__time-flex">
          <IoMdTime /> {value}
        </span>
        <GoPencil />
      </div>

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            className="appointments-client-info-modal__time-popup"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <div className="appointments-client-info-modal__time-grid">
              <div className="time-column">
                <button onClick={() => increase("hour")}><FaAngleUp /></button>
                <span>{String(hours).padStart(2, "0")}</span>
                <button onClick={() => decrease("hour")}><FaAngleDown /></button>
              </div>
              <span className="colon">:</span>
              <div className="time-column">
                <button onClick={() => increase("minute")}><FaAngleUp /></button>
                <span>{String(minutes).padStart(2, "0")}</span>
                <button onClick={() => decrease("minute")}><FaAngleDown /></button>
              </div>
              <div className="time-column ampm">
                <button onClick={() => increase("ampm")}><FaAngleUp /></button>
                <span>{ampm}</span>
                <button onClick={() => decrease("ampm")}><FaAngleDown /></button>
              </div>
            </div>
            <div className="appointments-client-info-modal__time-buttons">
              <button onClick={() => setOpen(false)}>Cancel</button>
              <button onClick={handleSave}>Save</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DurationPicker({ value = { hours: 1, minutes: 0 }, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(value.hours);
  const [minutes, setMinutes] = useState(value.minutes);

  const increase = (t) => t === "hour" ? setHours(h => h === 12 ? 0 : h + 1) : setMinutes(m => m + 5 >= 60 ? 0 : m + 5);
  const decrease = (t) => t === "hour" ? setHours(h => h === 0 ? 12 : h - 1) : setMinutes(m => m - 5 < 0 ? 55 : m - 5);
  const handleSave = () => { onChange({ hours, minutes }); setOpen(false); };

  const durationPickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (durationPickerRef.current && !durationPickerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className="appointments-client-info-modal__time-dropdown" ref={durationPickerRef}>
      <div
        className={`appointments-client-info-modal__time-input ${disabled ? "disabled" : ""}`}
        onClick={() => !disabled && setOpen(!open)}
      >
        <span className="appointments-client-info-modal__time-flex">
          <MdOutlineTimer /> {hours}h {minutes}m
        </span>
        <GoPencil />
      </div>

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            className="appointments-client-info-modal__time-popup"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <div className="appointments-client-info-modal__time-grid">
              <div className="time-column">
                <button onClick={() => increase("hour")}><FaAngleUp /></button>
                <span>{String(hours).padStart(2, "0")}</span>
                <button onClick={() => decrease("hour")}><FaAngleDown /></button>
              </div>
              <span className="colon">h</span>
              <div className="time-column">
                <button onClick={() => increase("minute")}><FaAngleUp /></button>
                <span>{String(minutes).padStart(2, "0")}</span>
                <button onClick={() => decrease("minute")}><FaAngleDown /></button>
              </div>
              <span className="colon">m</span>
            </div>
            <div className="appointments-client-info-modal__time-buttons">
              <button onClick={() => setOpen(false)}>Cancel</button>
              <button onClick={handleSave}>Save</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function EditClientModalCalendar({ isOpen, onClose, event, onSaveSuccess, userCurrency }) {
  const token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({ 
    date: "", 
    time: "08:00 AM", 
    duration: { hours: 1, minutes: 0 }, 
    services: [] 
  });
  const [availableServices, setAvailableServices] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [userCurrencyCode, setUserCurrencyCode] = useState(userCurrency || "USD");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const fetchAllData = async () => {
    if (!token) return;
    try {
      const headers = buildHeaders(token);
      
      const userRes = await axios.get(`${API_BASE}/auth/userinfo/`, { headers });
      const userData = userRes.data;
      setUserStatus(userData?.user?.status || null);
      
      const currency = userData?.currency || "USD";
      setUserCurrencyCode(currency);

      if (userData?.user?.status !== "individual") {
        const endpoints = ["/workers/", "/worker/", "/auth/workers/"];
        for (const ep of endpoints) {
          try {
            const res = await axios.get(`${API_BASE}${ep}`, { headers });
            if (res.status === 200 && Array.isArray(res.data)) { 
              setWorkers(res.data); 
              break; 
            }
          } catch { }
        }
      }
    } catch (err) { 
      console.error("❌ Error fetching user/workers:", err); 
    }
  };

  const fetchServices = async () => {
    if (!token) return;
    try {
      const headers = buildHeaders(token);
      
      const res = await axios.get(`${API_BASE}/service/my-services/`, { headers });
      if (res.status === 200) {
        const services = Array.isArray(res.data) ? res.data : [];
        const servicesWithCurrency = services.map(service => ({
          ...service,
          currency: service.currency || userCurrencyCode,
          currencySymbol: getCurrencySymbol(service.currency || userCurrencyCode)
        }));
        setAvailableServices(servicesWithCurrency);
      }
    } catch (err) { 
      console.error("❌ Failed to fetch services:", err); 
    }
  };

  useEffect(() => { 
    if (isOpen) { 
      fetchAllData(); 
      fetchServices(); 
    } 
  }, [isOpen]);

  useEffect(() => {
    if (userCurrency) {
      setUserCurrencyCode(userCurrency);
    }
  }, [userCurrency]);

  useEffect(() => {
    if (event && isOpen) {
      
      let formattedDate = "";
      if (event.date) {
        try {
          const dateStr = event.date;
          let dateObj;
          if (dateStr.includes("T")) {
            dateObj = new Date(dateStr);
          } else if (dateStr.includes("-")) {
            const [year, month, day] = dateStr.split("-").map(Number);
            dateObj = new Date(year, month - 1, day);
          } else {
            dateObj = new Date(dateStr);
          }
          
          if (!isNaN(dateObj.getTime())) {
            const monthNames = [
              "January", "February", "March", "April", "May", "June", 
              "July", "August", "September", "October", "November", "December"
            ];
            formattedDate = `${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}`;
          }
        } catch (error) {
          console.error("Ошибка форматирования даты:", error, event.date);
        }
      }

      let formattedTime = "08:00 AM";
      const startTime = event.start_time || event.timeStart || event.timeStart24h;
      const endTime = event.end_time || event.timeEnd || event.timeEnd24h;
      
      if (startTime) {
        try {
          const parsed = parseTimeString(startTime);
          formattedTime = formatTimeForDisplay(parsed.hour, parsed.minute, parsed.ampm);
        } catch (error) {
          console.error("Ошибка форматирования времени:", error, startTime);
        }
      }

      let durationObj = { hours: 1, minutes: 0 };
      if (startTime && endTime) {
        try {
          durationObj = calculateDuration(startTime, endTime);
        } catch (error) {
          console.error("Ошибка вычисления длительности:", error);
        }
      }

      let parsedServices = [];
      if (Array.isArray(event.services) && event.services.length > 0) {
        parsedServices = event.services.map(service => ({
          ...service,
          currency: service.currency || event.currency || userCurrencyCode,
          currencySymbol: getCurrencySymbol(service.currency || event.currency || userCurrencyCode)
        }));
      } else if (event.service) {
        parsedServices = [{ 
          id: event.service_id || event.id || Date.now(), 
          name: event.service, 
          price: event.price || 0,
          currency: event.currency || userCurrencyCode,
          currencySymbol: getCurrencySymbol(event.currency || userCurrencyCode)
        }];
      }

      setFormData({ 
        date: formattedDate, 
        time: formattedTime, 
        duration: durationObj, 
        services: parsedServices 
      });
    }
  }, [event, isOpen, userCurrencyCode]);

  const handleAddService = async () => {
    if (!availableServices.length) return toast.error("No services available");
    if (!event?.id) return toast.error("No appointment ID");

    const newService = availableServices[0]; 
    const serviceCurrency = newService.currency || userCurrencyCode;

    try {
      const headers = buildHeaders(token);
      
      const res = await axios.post(
        `${API_BASE}/api/client/appointments/${event.id}/add-service/`,
        { 
          service: newService.id, 
          duration: 30, 
          time_unit: "minutes", 
          price: newService.price, 
          currency: serviceCurrency 
        },
        { headers }
      );

      const addedServiceId = res.data?.id;
      const addedService = { 
        ...newService, 
        id: addedServiceId || newService.id,
        currency: serviceCurrency,
        currencySymbol: getCurrencySymbol(serviceCurrency)
      };

      setFormData(prev => ({ ...prev, services: [...prev.services, addedService] }));
      setEditingIndex(formData.services.length);
      toast.success(`Service "${newService.name}" added successfully!`);
    } catch (err) {
      console.error("❌ Add service failed:", err.response?.data || err.message);
      toast.error("Failed to add service");
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!event?.id) return toast.error("No appointment ID");

    setFormData(prev => ({ ...prev, services: prev.services.filter(s => s.id !== serviceId) }));

    try {
      const headers = buildHeaders(token);
      
      await axios.delete(`${API_BASE}/api/client/added-services/${serviceId}/delete/`, { headers });
      toast.success("Service removed successfully!");
    } catch (err) {
      console.error("❌ Delete service failed:", err.response?.data || err.message);
      toast.error("Failed to delete service");
    }
  };

  const handleEditService = index => setEditingIndex(index);

  const handleSaveServiceEdit = async (index, newServiceId) => {
    const selected = availableServices.find(s => s.id === Number(newServiceId));
    if (!selected || !event?.id) return;

    const serviceCurrency = selected.currency || userCurrencyCode;

    try {
      const headers = buildHeaders(token);
      
      await axios.post(`${API_BASE}/api/client/appointments/${event.id}/add-service/`,
        { 
          service: selected.id, 
          duration: 30, 
          time_unit: "minutes", 
          price: selected.price, 
          currency: serviceCurrency 
        },
        { headers }
      );

      setFormData(prev => ({
        ...prev,
        services: prev.services.map((s, i) => 
          i === index ? { 
            ...s, 
            id: s.id, 
            ...selected,
            currency: serviceCurrency,
            currencySymbol: getCurrencySymbol(serviceCurrency)
          } : s
        )
      }));
      setEditingIndex(null);
      toast.success(`Service updated to "${selected.name}"`);
    } catch (err) {
      console.error("❌ Edit service failed:", err.response?.data || err.message);
      toast.error("Failed to update service");
    }
  };

  const handleCancelAppointment = async (id) => {
    if (!id) return toast.error("No appointment ID provided");
    try {
      if (!token) return toast.error("No access token found.");
      
      const headers = buildHeaders(token);
      
      await axios.post(`${API_BASE}/api/client/appointment-cancel/`, { id }, { headers });
      toast.success("Appointment canceled successfully!");
      onClose();
      if (typeof onSaveSuccess === "function") onSaveSuccess();
    } catch (err) {
      console.error("❌ Cancel failed:", err.response?.data || err.message);
      toast.error(err.response?.data?.detail || "Failed to cancel appointment");
    }
  };

  const handleSave = async () => {
    toast.error("Editing date/time/duration is disabled in this mode!");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="appointments-client-info-modal__overlay" 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
        >
          <motion.div 
            ref={modalRef}
            className="appointments-client-info-modal__modal" 
            initial={{ scale: 0.95, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }} 
            exit={{ scale: 0.95, opacity: 0 }}
          >

            <div className="appointments-client-info-modal__header">
              <div className="appointments-client-info-modal__client-info-wrapper">
                <img src={event?.avatar || "/default-avatar.png"} alt="client" className="appointments-client-info-modal__client-avatar" />
                <div className="appointments-client-info-modal__client-info">
                  <div className="appointments-client-info-modal__client-header">
                    <h3>{event?.name || "Client"}</h3>
                    <button className="appointments-client-info-modal__profile-btn">Profile</button>
                  </div>
                  <p className="appointments-client-info-modal__client-description mt-2">{event?.notes || event?.comment || 'Requested a "classic taper"...'}</p>
                </div>
              </div>
              <button className="appointments-client-info-modal__close-btn" onClick={onClose}>
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {userStatus !== "individual" && (
              <div className="appointments-client-info-modal__master-row mt-2">
                <div className="appointments-client-info-modal__master-info">
                  <img src={event?.worker_avatar} alt="master" className="appointments-client-info-modal__master-avatar" />
                  <span>{event?.master || "Master"}</span>
                </div>
                <div className="appointments-client-info-modal__barber-tag">{event?.role || "Barber"}</div>
              </div>
            )}

            <div className="appointments-client-info-modal__inputs-row">
              <div className="appointments-client-info-modal__input-field">
                <label>Date</label>
                <CalendarDropdown 
                  key={`date-${event?.id || 'new'}`} 
                  value={formData.date} 
                  onChange={v => setFormData({ ...formData, date: v })} 
                  disabled={true} 
                />
              </div>
              <div className="appointments-client-info-modal__input-field">
                <label>Starting hour</label>
                <CustomTimePicker 
                  key={`time-${event?.id || 'new'}`} 
                  value={formData.time} 
                  onChange={v => setFormData({ ...formData, time: v })} 
                  disabled={true} 
                />
              </div>
              <div className="appointments-client-info-modal__input-field">
                <label>Duration</label>
                <DurationPicker 
                  key={`duration-${event?.id || 'new'}`} 
                  value={formData.duration} 
                  onChange={v => setFormData({ ...formData, duration: v })} 
                  disabled={true} 
                />
              </div>
            </div>

            <div className="appointments-client-info-modal__service-section">
              <label>Service</label>
              {formData.services.length > 0 ? formData.services.map((service, index) => (
                <div key={service.id || index} className="appointments-client-info-modal__service-item-box">
                  <div className="appointments-client-info-modal__service-display">
                    {editingIndex === index ? (
                      <select 
                        className="appointments-client-info-modal__service-select" 
                        value={service.id} 
                        onChange={e => handleSaveServiceEdit(index, e.target.value)}
                      >
                        {availableServices.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.name} - {formatPriceWithCurrency(s.price, s.currency)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="appointments-client-info-modal__service-name">
                        {service.name || "Service not specified"}
                        {service.price ? ` - ${formatPriceWithCurrency(service.price, service.currency)}` : ""}
                      </span>
                    )}
                    {editingIndex === index ? (
                      <button 
                        className="appointments-client-info-modal__service-save" 
                        onClick={() => setEditingIndex(null)}
                      >
                        Save
                      </button>
                    ) : (
                      <GoPencil 
                        className="appointments-client-info-modal__service-edit" 
                        onClick={() => handleEditService(index)} 
                      />
                    )}
                  </div>
                  {index === formData.services.length - 1 ? (
                    <button className="appointments-client-info-modal__service-add" onClick={handleAddService}>
                      <GoPlus />
                    </button>
                  ) : (
                    <button 
                      className="appointments-client-info-modal__service-delete" 
                      onClick={() => handleDeleteService(service.id)}
                    >
                      <GoTrash />
                    </button>
                  )}
                </div>
              )) : (
                <div className="appointments-client-info-modal__service-item-box">
                  <div className="appointments-client-info-modal__service-display">
                    <span className="appointments-client-info-modal__service-name">
                      {event?.service || "Service not specified"}
                      {event?.price ? ` - ${formatPriceWithCurrency(event.price, event.currency)}` : ""}
                    </span>
                    <GoPencil 
                      className="appointments-client-info-modal__service-edit" 
                      onClick={handleAddService} 
                    />
                  </div>
                  <button className="appointments-client-info-modal__service-add" onClick={handleAddService}>
                    <GoPlus />
                  </button>
                </div>
              )}
            </div>

            <div className="appointments-client-info-modal__footer">
              <button 
                className="appointments-client-info-modal__cancel-btn" 
                onClick={() => handleCancelAppointment(event?.id)}
              >
                Cancel appointment
              </button>
              <button className="appointments-client-info-modal__save-btn" onClick={handleSave}>
                Save changes
              </button>
            </div>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}